import { FormEvent, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Monitor, Smartphone, Trash2 } from 'lucide-react';
import { authApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Fehlermeldung } from './Auth/AuthLayout';

const eingabe =
  'mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

function Stammdaten() {
  const { benutzer, neuLaden } = useAuth();
  const [daten, setDaten] = useState({
    vorname: benutzer?.vorname ?? '',
    nachname: benutzer?.nachname ?? '',
    titel: benutzer?.titel ?? '',
    telefon: benutzer?.telefon ?? '',
  });
  const [fehler, setFehler] = useState<unknown>(null);
  const [gespeichert, setGespeichert] = useState(false);

  const speichern = useMutation({
    mutationFn: () => authApi.ichAendern(daten),
    onSuccess: async () => {
      setFehler(null);
      setGespeichert(true);
      await neuLaden();
    },
    onError: (e) => {
      setFehler(e);
      setGespeichert(false);
    },
  });

  function feld(name: keyof typeof daten) {
    return {
      value: daten[name],
      onChange: (e: { target: { value: string } }) => {
        setGespeichert(false);
        setDaten((d) => ({ ...d, [name]: e.target.value }));
      },
    };
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6">
      <h2 className="text-base font-semibold text-gray-900">Meine Daten</h2>
      <p className="mt-1 text-sm text-gray-600">
        Diese Angaben erscheinen in Berichten und Prüfprotokollen.
      </p>

      <form
        className="mt-4 space-y-4"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          speichern.mutate();
        }}
      >
        <Fehlermeldung fehler={fehler} />

        <div>
          <label className="block text-sm font-medium text-gray-700">E-Mail</label>
          {/* Nicht aenderbar: die Adresse ist die Anmeldung und das Ziel der
              Bestaetigungscodes. Eine Aenderung braucht einen eigenen,
              bestaetigten Ablauf. */}
          <input
            disabled
            value={benutzer?.email ?? ''}
            className={`${eingabe} bg-gray-50 text-gray-500`}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Vorname</label>
            <input className={eingabe} {...feld('vorname')} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Nachname</label>
            <input required className={eingabe} {...feld('nachname')} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">Titel</label>
            <input className={eingabe} placeholder="Dipl.-Ing." {...feld('titel')} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Telefon</label>
            <input className={eingabe} {...feld('telefon')} />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={speichern.isPending}
            className="flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {speichern.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Speichern
          </button>
          {gespeichert && <span className="text-sm text-green-700">Gespeichert.</span>}
        </div>
      </form>
    </section>
  );
}

function PasswortAendern() {
  const [alt, setAlt] = useState('');
  const [neu, setNeu] = useState('');
  const [fehler, setFehler] = useState<unknown>(null);
  const [fertig, setFertig] = useState(false);

  const aendern = useMutation({
    mutationFn: () => authApi.passwortAendern({ altes_passwort: alt, neues_passwort: neu }),
    onSuccess: () => {
      setFehler(null);
      setFertig(true);
      setAlt('');
      setNeu('');
    },
    onError: setFehler,
  });

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6">
      <h2 className="text-base font-semibold text-gray-900">Passwort ändern</h2>
      <p className="mt-1 text-sm text-gray-600">
        Alle anderen angemeldeten Geräte werden dabei abgemeldet.
      </p>

      <form
        className="mt-4 space-y-4"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          aendern.mutate();
        }}
      >
        <Fehlermeldung fehler={fehler} />
        {fertig && (
          <p className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
            Passwort geändert. Andere Geräte wurden abgemeldet.
          </p>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Bisheriges Passwort
          </label>
          <input
            type="password"
            required
            autoComplete="current-password"
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
            className={eingabe}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Neues Passwort</label>
          <input
            type="password"
            required
            minLength={12}
            autoComplete="new-password"
            value={neu}
            onChange={(e) => setNeu(e.target.value)}
            className={eingabe}
          />
        </div>

        <button
          type="submit"
          disabled={aendern.isPending}
          className="flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
        >
          {aendern.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Passwort ändern
        </button>
      </form>
    </section>
  );
}

function Sitzungen() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['sitzungen'],
    queryFn: authApi.sitzungen,
  });

  const widerrufen = useMutation({
    mutationFn: (id: number) => authApi.sitzungWiderrufen(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sitzungen'] }),
  });
  const alleAnderen = useMutation({
    mutationFn: () => authApi.alleAnderenWiderrufen(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sitzungen'] }),
  });

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Angemeldete Geräte</h2>
          <p className="mt-1 text-sm text-gray-600">
            Erkennen Sie ein Gerät nicht wieder, beenden Sie die Sitzung und ändern
            Sie Ihr Passwort.
          </p>
        </div>
        <button
          type="button"
          onClick={() => alleAnderen.mutate()}
          className="shrink-0 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          Alle anderen abmelden
        </button>
      </div>

      {isLoading ? (
        <p className="mt-4 text-sm text-gray-500">Wird geladen…</p>
      ) : (
        <ul className="mt-4 divide-y divide-gray-100">
          {(data ?? []).map((s: any) => (
            <li key={s.id} className="flex items-center gap-3 py-3">
              {s.geraete_typ === 'WEB' ? (
                <Monitor className="h-5 w-5 text-gray-400" />
              ) : (
                <Smartphone className="h-5 w-5 text-gray-400" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">
                  {s.geraete_name || s.user_agent || 'Unbekanntes Gerät'}
                  {s.ist_aktuelle && (
                    <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-xs font-normal text-green-800">
                      dieses Gerät
                    </span>
                  )}
                </p>
                <p className="truncate text-xs text-gray-500">
                  {s.ip_adresse} · zuletzt aktiv{' '}
                  {s.letzte_aktivitaet
                    ? new Date(s.letzte_aktivitaet).toLocaleString('de-DE')
                    : 'unbekannt'}
                </p>
              </div>
              {!s.ist_aktuelle && (
                <button
                  type="button"
                  onClick={() => widerrufen.mutate(s.id)}
                  title="Sitzung beenden"
                  className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function ProfilPage() {
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-2xl font-black tracking-tight text-secondary-900">Mein Profil</h1>
      <Stammdaten />
      <PasswortAendern />
      <Sitzungen />
    </div>
  );
}
